import { Box, styled } from '@mui/material';
import * as Sentry from '@sentry/react';
import { FallbackRender } from '@sentry/react';
import React from 'react';

import { colorSystem, NAVBAR_HEIGHT } from '../../../theme';
import { CenteredSpinner } from '../../CircularProgress';
import { Flexbox } from '../../Flexbox';
import { Skeleton } from '../../Skeleton';
import { StandardLayoutProps } from '../../types';

interface SidebarLayoutProps extends Omit<StandardLayoutProps, 'content'> {
    toolbar?: React.ReactNode;
    sidebar?: React.ReactNode;
    content: React.ReactNode;
    defaultSidebarWidth?: string;
    fallback: FallbackRender;
}
const sidebarBackground = colorSystem.neutral.white;

export function SidebarLayout({
    sidebar,
    content,
    toolbar,
    defaultSidebarWidth = '240px',
    fallback,
    ...props
}: SidebarLayoutProps): JSX.Element {
    const sidebarWidth = sidebar ? defaultSidebarWidth : '0px';
    const dividerWidth = sidebar ? '1px' : '0px';

    // As tall as the parent, not the viewport: the toolbar row, then the sidebar and content, each scrolling.
    return (
        <Container {...props}>
            <SuspenseBoundary>
                {toolbar && (
                    <ToolbarContainer style={{ height: NAVBAR_HEIGHT }} id="layout-toolbar">
                        {toolbar}
                    </ToolbarContainer>
                )}
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        display: 'grid',
                        gridTemplateColumns: `${sidebarWidth} ${dividerWidth} 1fr`,
                        gridTemplateRows: 'minmax(0, 1fr)',
                    }}
                >
                    <SidebarContainer>
                        <Sentry.ErrorBoundary fallback={fallback}>
                            <React.Suspense
                                fallback={
                                    <Flexbox
                                        gap="8px"
                                        style={{ background: sidebarBackground }}
                                        sx={{
                                            flexDirection: 'column',
                                            padding: '20px',
                                        }}
                                    >
                                        <Skeleton width={'100%'} height="40px" />
                                        <Skeleton width={'100%'} height="40px" />
                                        <Skeleton width={'100%'} height="40px" />
                                        <Skeleton width={'100%'} height="40px" />
                                    </Flexbox>
                                }
                            >
                                {sidebar}
                            </React.Suspense>
                        </Sentry.ErrorBoundary>
                    </SidebarContainer>

                    <div style={{ background: colorSystem.neutral[2] }} />

                    <Sentry.ErrorBoundary fallback={fallback}>
                        <SuspenseBoundary>
                            <ContentContainer id="scrollContainer">{content}</ContentContainer>
                        </SuspenseBoundary>
                    </Sentry.ErrorBoundary>
                </Box>
            </SuspenseBoundary>
        </Container>
    );
}

const Container = styled(Box)({
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
});

function SuspenseBoundary({ children, minHeight }: { children: React.ReactNode; minHeight?: string }): JSX.Element {
    return (
        <React.Suspense
            fallback={
                <CenteredSpinner
                    sx={{
                        minHeight: minHeight,
                    }}
                />
            }
        >
            {children}
        </React.Suspense>
    );
}

const ToolbarContainer = styled('div')({
    width: '100%',
    flexShrink: 0,
    overflow: 'hidden',
    borderBottom: `1px solid ${colorSystem.neutral[2]}`,
    boxSizing: 'border-box',
});

const SidebarContainer = styled('div')({
    overflow: 'auto',
    background: sidebarBackground,
});

const ContentContainer = styled('div')({
    overflow: 'auto',
    minWidth: '724px',
    position: 'relative',
    background: colorSystem.neutral.white,
});
